import { json, LoaderFunctionArgs, type MetaFunction } from "@remix-run/node";
import { Link, useLoaderData } from "@remix-run/react";
import { apiRequest } from "~/apiclient";
import { Gremlin } from "~/components/Gremlin";
import StreamerCard from "~/components/Streamer";
import Toast from "~/components/Toast";
import { default as TradeComponent } from "~/components/Trade";
import { isAuthenticated } from "~/services/auth.server";
import { commitSession, getSession } from "~/session.server";
import type { PaginatedResponse, Streamer, Trade, UserGremlin } from "~/types";

export const meta: MetaFunction = () => [{ title: "Home | Gremgacha" }];

export const loader = async ({ request }: LoaderFunctionArgs) => {
  let tradesData: PaginatedResponse<Trade> = { count: 0, results: [] };
  let marketData: PaginatedResponse<UserGremlin> = { count: 0, results: [] };
  let pullsData: UserGremlin[] = [];

  const user = await isAuthenticated(request);
  const streamers: Streamer[] = await apiRequest({ endpoint: "streamers" });

  if (user) {
    [tradesData, marketData, pullsData] = await Promise.all([
      apiRequest({
        endpoint: "trades",
        queryParams: { status: "PENDING", limit: "3" },
        token: user.key,
      }),
      apiRequest({
        endpoint: "usergremlins/my-wishlist",
        queryParams: { limit: "10", offset: "0", randomize: "true" },
        token: user.key,
      }),
      apiRequest({
        endpoint: "usergremlins/latest",
        queryParams: { limit: "10", offset: "0" },
        token: user.key,
      }),
    ]);
  }

  const session = await getSession(request.headers.get("Cookie"));
  const successMessage = session.get("successMessage") || "";
  const errorMessage = session.get("errorMessage") || "";

  return json(
    {
      streamers: streamers,
      trades: tradesData.results,
      market: marketData.results,
      pulls: pullsData,
      user,
      successMessage,
      errorMessage,
    },
    {
      headers: {
        "Set-Cookie": await commitSession(session),
        "Cache-Control": "private, max-age=5s",
      },
    },
  );
};

export default function Index() {
  const data = useLoaderData<typeof loader>();

  return (
    <div className="p-4 mb-12 lg:mb-0">
      {data.successMessage ? (
        <Toast success={true} message={data.successMessage} />
      ) : null}
      {data.errorMessage ? <Toast success={false} message={data.errorMessage} /> : null}
      <h2 className="pb-4 text-4xl font-semibold">Recently added streamers:</h2>
      <div className="scrollbar-none mb-8 flex flex-row gap-8 overflow-x-auto 2xl:mb-12">
        {data.streamers.map((streamer) => (
          <StreamerCard key={streamer.id} streamer={streamer} />
        ))}
      </div>
      <div className="flex flex-col justify-between gap-y-12 w-full">
        <div className="flex flex-col w-full">
          <h2 className="pb-4 text-4xl font-semibold">Your pending trades:</h2>
          {data.user == null ? (
            <p>
              Please{" "}
              <Link to="/login" className="font-bold">
                log in
              </Link>{" "}
              to see your trades
            </p>
          ) : data.trades.length === 0 ? (
            <p>no pending trades yippee</p>
          ) : (
            <div className="flex flex-col gap-y-4">
              {data.trades.map((trade) => (
                <Link to={`/trades/${trade.id}`} key={trade.id} prefetch="intent">
                  <TradeComponent
                    key={trade.id}
                    sentByMe={data.user?.username === trade.offering_user.display_name}
                    offeringUserProfilePic={trade.offering_user.profile_pic}
                    requestedUserProfilePic={trade.requested_user.profile_pic}
                    offers={trade.offers}
                    requests={trade.requests}
                    date={trade.date_created}
                  />
                </Link>
              ))}
              <Link to="/trades" prefetch="intent" className="underline">
                View the rest
              </Link>
            </div>
          )}
        </div>
        <div className="flex flex-col w-full">
          <h2 className="pb-4 text-4xl font-semibold">
            You might be interested in these:
          </h2>
          {data.user == null ? (
            <p>
              Please{" "}
              <Link to="/login" className="font-bold">
                log in
              </Link>{" "}
              to see suggestions from your wishlists
            </p>
          ) : data.market.length === 0 ? (
            <p>Nothing over here... Go wishlist some grems and check back later!</p>
          ) : data.market.length > 0 ? (
            <>
              <div className="grow">
                <div className="flex flex-row items-stretch gap-x-4 overflow-x-scroll pb-4">
                  {data.market.map((ug) => (
                    <Link
                      key={`wishlist-${ug.id}`}
                      to={`/user/${ug.user.display_name}/collection?streamer=${ug.gremlin.streamer.username}`}
                    >
                      <Gremlin
                        name={ug.gremlin.name}
                        owner={ug.user.display_name}
                        imageUrl={ug.gremlin.image_url}
                        thumbnailUrl={ug.gremlin.image_thumbnail}
                      />
                    </Link>
                  ))}
                </div>
              </div>
              <Link
                to="/marketplace/search?searchType=wishlist"
                prefetch="intent"
                className="underline mt-2 lg:mt-4"
              >
                Browse more like these
              </Link>
            </>
          ) : null}
        </div>
        {data.user != null ? (
          <div className="flex flex-col gap-y-4">
            <h2 className="text-4xl font-semibold">Recent pulls</h2>
            <div className="flex flex-row gap-x-4 overflow-x-scroll pb-4">
              {data.pulls.map((ug) => (
                <Link
                  key={`pull-${ug.id}`}
                  to={
                    data.user?.twitchId === ug.user.twitch_id
                      ? "/gremlins"
                      : `/user/${ug.user.display_name}/collection?streamer=${ug.gremlin.streamer.username}`
                  }
                >
                  <Gremlin
                    name={ug.gremlin.name}
                    owner={ug.user.display_name}
                    lastAcquired={ug.date_created}
                    imageUrl={ug.gremlin.image_url}
                    thumbnailUrl={ug.gremlin.image_thumbnail}
                  />
                </Link>
              ))}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
